/**
 * ReThinker API Client
 *
 * Centralized API layer covering all endpoints:
 *   - Production API (/api/*) — auth, ideas, conversations, scratchpad, agent, admin, analytics
 *   - Brain API (scintillating-flow) — daemon state, task queue, health
 *
 * Features:
 *   - ApiError class with type classification (Network/Server/Client)
 *   - Retry with exponential backoff + jitter on retryable errors
 *   - Cookie-based auth (credentials: 'include')
 *   - Typed interfaces for all response shapes
 */

// --- Configuration ---

const API_BASE = import.meta.env.VITE_API_URL || '/api'

const BRAIN_URL = import.meta.env.VITE_BRAIN_URL
  || 'https://scintillating-flow-production-4363.up.railway.app'

// --- Error Types ---

export type ApiErrorType = 'NetworkError' | 'ServerError' | 'ClientError'

export class ApiError extends Error {
  public readonly type: ApiErrorType
  public readonly status: number | null
  public readonly detail: string
  public readonly retryable: boolean

  constructor(type: ApiErrorType, message: string, status: number | null = null, detail?: string) {
    super(message)
    this.name = 'ApiError'
    this.type = type
    this.status = status
    this.detail = detail || message
    this.retryable = type === 'NetworkError' || (status !== null && [502, 503].includes(status))
  }

  get userMessage(): string {
    switch (this.type) {
      case 'NetworkError':
        return 'Unable to reach the server. Check your connection and try again.'
      case 'ServerError':
        return this.status === 502 || this.status === 503
          ? 'The server is temporarily unavailable. Retrying...'
          : `Server error (${this.status}). Please try again later.`
      case 'ClientError':
        return this.detail
    }
  }
}

function classifyError(error: unknown, status?: number, detail?: string): ApiError {
  if (error instanceof ApiError) return error

  if (error instanceof TypeError && error.message.includes('fetch')) {
    return new ApiError('NetworkError', error.message, null, 'Network request failed')
  }

  if (status !== undefined) {
    if (status >= 500) return new ApiError('ServerError', detail || `HTTP ${status}`, status, detail)
    if (status >= 400) return new ApiError('ClientError', detail || `HTTP ${status}`, status, detail)
  }

  return new ApiError('NetworkError', String(error), null, 'An unexpected error occurred')
}

// --- Retry Logic ---

async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 2,
  baseDelay: number = 500,
): Promise<T> {
  let lastError: ApiError | undefined

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      const apiError = error instanceof ApiError ? error : classifyError(error)
      lastError = apiError

      if (!apiError.retryable || attempt === maxRetries) throw apiError

      const delay = baseDelay * Math.pow(2, attempt) + Math.random() * baseDelay
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }

  throw lastError!
}

// --- Core Fetch ---

async function fetchAPI<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
  return withRetry(async () => {
    let response: Response
    try {
      response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      })
    } catch (error) {
      throw classifyError(error)
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({ detail: 'Request failed' }))
      // detail may be a string or an object like { error_code, message }
      const rawDetail = body.detail || `HTTP ${response.status}`
      const detail = typeof rawDetail === 'object'
        ? rawDetail.message || rawDetail.error_code || JSON.stringify(rawDetail)
        : String(rawDetail)
      throw classifyError(new Error(detail), response.status, detail)
    }

    // Guard against SPA catch-all returning HTML instead of JSON
    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      throw new ApiError('ServerError', 'API returned non-JSON response (endpoint may not exist)', 502)
    }

    return response.json()
  })
}

async function fetchBrain<T = unknown>(endpoint: string, options?: RequestInit): Promise<T> {
  return withRetry(async () => {
    let response: Response
    try {
      response = await fetch(`${BRAIN_URL}${endpoint}`, {
        headers: { 'Content-Type': 'application/json' },
        ...options,
      })
    } catch (error) {
      throw classifyError(error)
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({ detail: 'Request failed' }))
      throw classifyError(new Error(body.detail), response.status, body.detail)
    }

    return response.json()
  })
}

// ============================================================================
// Types
// ============================================================================

export interface User {
  user_id: string
  email: string
  role: 'user' | 'admin'
  is_agent?: boolean
  api_balance: number
  approval_status: string
  created_at: string
}

export interface UserProfile {
  user: User
  track_record: {
    todos_resolved: number
    todos_by_idea: Record<string, number>
    todos_pending_system: number
    ideas_created: number
    conversations: number
    comments: number
  }
  ideas: Array<{ id: string; title: string; created_at: string | null }>
  recent_activity: Array<{
    eventId: string
    eventType: string
    timestamp: string | null
    context: Record<string, unknown>
    sourceAgent: string | null
    severity: string | null
  }>
  recent_resolutions: Array<{
    todo_id: string
    idea: string | null
    question: string
    todo_type: string | null
    addressed_at: string | null
    response: Record<string, unknown> | null
  }>
  member_since: string | null
  // Agent-only fields
  agent_state?: Record<string, unknown>
  agent_audit?: Array<{
    timestamp: string | null
    method: string
    path: string
    status_code: number | null
    policy_result: string
  }>
}

export interface Idea {
  idea_id: string
  user_id: string
  title: string
  raw_goal?: string
  archetype_id?: string
  canon: { CB: string; SP: string; BP: string }
  goal_profile: {
    archetype_id: string
    scope: string
    intent: string
    stakes: string
    audience: string[]
  }
  goal_history?: Array<{ timestamp: string; change_summary: string; reason: string }>
  created_at: string
  updated_at: string
}

export interface Conversation {
  id: string
  idea_id: string | null
  title: string
  mode: 'NORMAL_CHAT' | 'CANON_BRAINSTORM' | 'AGENT_ACTIVITY'
  created_at: string
  updated_at: string
  first_message_preview?: string | null
}

export interface Message {
  id: string
  conversation_id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  mode_at_time?: string
  created_at: string
  metadata?: Record<string, unknown>
}

export interface AgentMemory {
  id: string
  goals: {
    epics?: Array<string | { id: string; title: string; status: string; tasks: unknown[] }>
    active_project?: string | null
    projects?: Record<string, unknown>
    [key: string]: unknown
  }
  decisions: Array<{
    timestamp: string
    type: string
    question?: string
    output: string
    rationale?: string
    impact?: string
  }>
  current_activity: {
    task_type?: string
    task_id?: string
    status?: string
    progress?: number
    started_at?: string
    description?: string
    focus?: string
    details?: Record<string, unknown>
  }
  orchestration_state: {
    gpu_tasks: unknown[]
    research_tasks: unknown[]
    scheduled_jobs: unknown[]
    token_budget: { used_today: number; limit_daily: number }
  }
}

export interface AgentTask {
  id: string
  task_type: string
  description: string
  status: string
  priority: string
  params: Record<string, unknown>
  result?: Record<string, unknown>
  error?: string
  created_at: string
  started_at?: string
  completed_at?: string
  claimed_by?: string
  parent_task_id?: string
  workflow_chain?: string[]
}

export interface AgentDecision {
  id: string
  question: string
  options: string[]
  context?: string
  idea?: string
  blocking?: string[]
  agent_reasoning?: string
  status: string
  selected_option?: number
  rationale?: string
  created_at: string
  resolved_at?: string
}

export interface Scratchpad {
  idea_id: string
  content: string
  updated_at: string
}

export interface TestResult {
  test_id: string
  test_name: string
  status: 'RUNNABLE' | 'UNRUNNABLE'
  model?: string
  reason?: string
}

export interface Archetype {
  id: string
  label: string
  description: string
}

export interface BrainHealth {
  status: string
  timestamp: string
  active_daemons: number
  pending_tasks: number
}

export interface DaemonState {
  daemon_id: string
  daemon_version: string
  current_phase: number
  loop_count: number
  active_task_id: string | null
  last_heartbeat: string
}

export interface TokenBudget {
  daily_budget_usd: number
  used_today_usd: number
  remaining_usd: number
  usage_percent: number
  status: string
  tokens_today: number
  calls_today: number
}

export interface SystemHealth {
  overall_status: string
  notifications: Array<{
    level: string
    message: string
    component: string
    recovery_eta?: string
  }>
  checked_at: string
}

export interface Comment {
  id: string
  idea_id: string
  user_id: string
  user_email: string
  content: string
  parent_id: string | null
  created_at: string
}

export interface FeaturedIdea {
  id: string
  title: string
  tagline: string
  problem: string
  solution: string
  collaboration_hooks: Array<{ question: string; context: string; invite: string }>
  canon: { CB: string; SP: string; BP: string }
  goal_profile: Idea['goal_profile']
  has_cb: boolean
  has_sp: boolean
  has_bp: boolean
}

export interface Claim {
  id: string
  type: string
  parents: string[]
  statement: string
  source: string
  section: string
  fidelity: number
  testable: boolean
  staleness_tags: string[]
}

export interface ClaimsResponse {
  idea: string
  claims: Claim[]
  count: number
  error?: string
}

export interface Feedback {
  id: string
  category: string
  severity?: string
  title: string
  description: string
  status: string
  vote_count: number
  created_at: string
}

// ============================================================================
// Auth
// ============================================================================

export const auth = {
  signup: (email: string, password: string) =>
    fetchAPI('/auth/signup', { method: 'POST', body: JSON.stringify({ email, password }) }),

  login: (email: string, password: string) =>
    fetchAPI('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),

  logout: () =>
    fetchAPI('/auth/logout', { method: 'POST' }),

  me: () =>
    fetchAPI<User>('/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    fetchAPI('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),
}

// ============================================================================
// Profile
// ============================================================================

export const profile = {
  me: () =>
    fetchAPI<UserProfile>('/users/me/profile'),
  get: (userId: string) =>
    fetchAPI<UserProfile>(`/users/${userId}/profile`),
}

// ============================================================================
// Ideas
// ============================================================================

export const ideas = {
  list: () =>
    fetchAPI<Idea[]>('/ideas'),

  create: (data: { title: string; raw_goal: string; archetype_id?: string }) =>
    fetchAPI<Idea>('/ideas', { method: 'POST', body: JSON.stringify(data) }),

  get: (id: string) =>
    fetchAPI<Idea>(`/ideas/${id}`),

  getForUser: (userId: string) =>
    fetchAPI<Idea[]>(`/users/${userId}/ideas`),

  updateCanon: (id: string, canon: { CB: string; SP: string; BP: string }) =>
    fetchAPI(`/ideas/${id}/canon`, { method: 'PUT', body: JSON.stringify(canon) }),

  generateCanon: (id: string) =>
    fetchAPI(`/ideas/${id}/generate-canon`, { method: 'POST' }),

  updateGoalProfile: (id: string, profile: Idea['goal_profile']) =>
    fetchAPI(`/ideas/${id}/goal-profile`, { method: 'PUT', body: JSON.stringify(profile) }),

  listShared: () =>
    fetchAPI<Array<Idea & { owner_email: string; role: string }>>('/ideas/shared-with-me'),

  getClaims: (id: string) =>
    fetchAPI<ClaimsResponse>(`/ideas/${id}/claims`),
}

// ============================================================================
// Conversations
// ============================================================================

export const conversations = {
  create: (data: { idea_id: string | null; title: string }) =>
    fetchAPI<Conversation>('/conversations', { method: 'POST', body: JSON.stringify(data) }),

  list: (ideaId?: string) =>
    fetchAPI<Conversation[]>(`/conversations${ideaId ? `?idea_id=${ideaId}` : ''}`),

  get: (id: string) =>
    fetchAPI<{ conversation: Conversation; messages: Message[] }>(`/conversations/${id}`),

  delete: (id: string) =>
    fetchAPI(`/conversations/${id}`, { method: 'DELETE' }),

  updateTitle: (id: string, title: string) =>
    fetchAPI<Conversation>(`/conversations/${id}`, { method: 'PATCH', body: JSON.stringify({ title }) }),

  sendMessage: (id: string, message: string) =>
    fetchAPI<{ user_message: Message | null; assistant_message: Message | null; error?: string }>
      (`/conversations/${id}/messages`, { method: 'POST', body: JSON.stringify({ message }) }),

  updateMode: (id: string, mode: string) =>
    fetchAPI(`/conversations/${id}/mode`, { method: 'PUT', body: JSON.stringify({ mode }) }),

  addSystemMessage: (id: string, content: string, taskId?: string) =>
    fetchAPI<Message>(`/conversations/${id}/system-message`, {
      method: 'POST', body: JSON.stringify({ content, task_id: taskId }),
    }),
}

// ============================================================================
// Scratchpad
// ============================================================================

export const scratchpad = {
  get: (ideaId: string) =>
    fetchAPI<Scratchpad>(`/scratchpads/${ideaId}`),

  save: (ideaId: string, content: string) =>
    fetchAPI<Scratchpad>(`/scratchpads/${ideaId}`, { method: 'PUT', body: JSON.stringify({ content }) }),
}

// ============================================================================
// Tests / Validation
// ============================================================================

export const tests = {
  run: (ideaId: string) =>
    fetchAPI<{ tests: TestResult[]; message?: string }>(`/ideas/${ideaId}/tests/run`, { method: 'POST' }),

  getHistory: (ideaId: string) =>
    fetchAPI(`/ideas/${ideaId}/tests/history`),
}

// ============================================================================
// Config (read-only lookups)
// ============================================================================

export const config = {
  getArchetypes: () =>
    fetchAPI<Archetype[]>('/config/goal-archetypes'),

  getTestHarnesses: () =>
    fetchAPI('/config/test-harnesses'),

  getUserQuiz: (archetypeId: string) =>
    fetchAPI(`/config/user-quiz?archetype_id=${archetypeId}`),
}

// ============================================================================
// Collaboration
// ============================================================================

export const collaboration = {
  share: (ideaId: string, email: string, role: string) =>
    fetchAPI(`/ideas/${ideaId}/collaborators`, {
      method: 'POST', body: JSON.stringify({ email, role }),
    }),

  getCollaborators: (ideaId: string) =>
    fetchAPI<{ owner: User; collaborators: Array<{ id: string; email: string; role: string }> }>
      (`/ideas/${ideaId}/collaborators`),

  removeCollaborator: (ideaId: string, collaboratorId: string) =>
    fetchAPI(`/ideas/${ideaId}/collaborators/${collaboratorId}`, { method: 'DELETE' }),
}

// ============================================================================
// Comments
// ============================================================================

export const comments = {
  list: (ideaId: string) =>
    fetchAPI<Comment[]>(`/ideas/${ideaId}/comments`),

  create: (ideaId: string, content: string, parentId: string | null = null) =>
    fetchAPI<Comment>(`/ideas/${ideaId}/comments`, {
      method: 'POST', body: JSON.stringify({ content, parent_id: parentId }),
    }),

  delete: (ideaId: string, commentId: string) =>
    fetchAPI(`/ideas/${ideaId}/comments/${commentId}`, { method: 'DELETE' }),
}

// ============================================================================
// Canon / Featured
// ============================================================================

export const canon = {
  getUserProjects: () =>
    fetchAPI<{ projects: unknown[]; meta_idea: unknown; featured_ideas?: unknown[] }>('/canon/user-projects'),

  getProjects: () =>
    fetchAPI<Array<{
      id: string
      title: string
      source: string
      has_cb: boolean
      has_sp: boolean
      has_bp: boolean
      stakes?: string
      canon_repo?: string
    }>>('/canon/projects'),

  getFeatured: () =>
    fetchAPI<FeaturedIdea[]>('/featured-ideas'),

  getFeaturedIdea: (ideaId: string) =>
    fetchAPI<FeaturedIdea>(`/featured-ideas/${ideaId}`),
}

// ============================================================================
// Admin (user management)
// ============================================================================

export const admin = {
  getUsers: () =>
    fetchAPI<User[]>('/admin/users'),

  adjustBalance: (userId: string, amount: number) =>
    fetchAPI(`/admin/users/${userId}/balance`, {
      method: 'POST', body: JSON.stringify({ amount_to_add: amount }),
    }),
}

// ============================================================================
// Homeostasis Dashboard Types
// ============================================================================

export interface WalkerState {
  phase?: string
  cycle?: number
  idea_index?: number
  idea_count?: number
  claims_processed?: number
  graph_mutations?: number
  mined_reviewed?: number
  mined_promoted?: number
  mined_pruned?: number
  mined_llm_rejected?: number
  mined_index?: number
  last_processed_at?: string
  idea_claim_counts?: Record<string, number>
  idea_finding_counts?: Record<string, number>
}

export interface MinerState {
  file_index?: number
  total_files?: number
  messages_processed?: number
  claims_found?: number
  messages_skipped?: number
  cycle?: number
  last_processed_at?: string
}

export interface QualityGateStats {
  total_evaluated?: number
  rejected?: number
  approved?: number
  noise_ratio?: number
}

export type TodoBreakdown = Record<string, number>

export interface SupervisorDaemon {
  pid?: number
  uptime_seconds?: number
  restarts?: number
}

export interface BudgetDetails {
  current_state?: string
  weekly_tokens_used?: number
  weekly_limit?: number
}

export interface HomeostasisSummary {
  // Legacy fields
  daemon_status?: string
  pending_todos?: number
  phase?: string
  budget_state?: string
  last_loop?: number

  // Homeostasis fields
  walker?: WalkerState
  miner?: MinerState
  quality_gate?: QualityGateStats
  todo_breakdown?: TodoBreakdown
  supervisor?: Record<string, SupervisorDaemon>
  budget?: BudgetDetails
  warnings?: string[]
}

// ============================================================================
// Agent System — Admin endpoints
// ============================================================================

export const agentAdmin = {
  // Dashboard summary (includes homeostasis data)
  getDashboardSummary: () =>
    fetchAPI<HomeostasisSummary>('/admin/agent/dashboard/summary'),

  // Memory
  getMemory: () =>
    fetchAPI<AgentMemory>('/admin/agent/memory'),

  updateMemory: (data: Partial<AgentMemory>) =>
    fetchAPI('/admin/agent/memory', { method: 'PUT', body: JSON.stringify(data) }),

  // Tasks
  getTasks: (status?: string) =>
    fetchAPI<{ tasks: AgentTask[] }>(`/admin/agent/tasks${status ? `?status=${status}` : ''}`),

  getPendingTasks: () =>
    fetchAPI<{ tasks: AgentTask[] }>('/admin/agent/tasks/pending'),

  createTask: (data: { task_type: string; params: Record<string, unknown>; priority?: string }) =>
    fetchAPI('/admin/agent/tasks', { method: 'POST', body: JSON.stringify(data) }),

  updateTask: (id: string, data: { status: string; result?: unknown; error?: string }) =>
    fetchAPI(`/admin/agent/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  cancelTask: (id: string) =>
    fetchAPI(`/admin/agent/tasks/${id}`, { method: 'DELETE' }),

  retryTask: (id: string) =>
    fetchAPI<{ message: string; new_task_id: string }>(`/admin/agent/tasks/${id}/retry`, { method: 'POST' }),

  resetTask: (id: string) =>
    fetchAPI(`/admin/agent/tasks/${id}/reset`, { method: 'POST' }),

  cleanupStaleTasks: (taskType?: string) =>
    fetchAPI(`/admin/agent/tasks/cleanup/stale${taskType ? `?task_type=${taskType}` : ''}`, { method: 'DELETE' }),

  getTaskDetail: (id: string) =>
    fetchAPI(`/admin/agent/tasks/${id}/detail`),

  getTaskStats: () =>
    fetchAPI<{ total: number; by_status: Record<string, number>; by_type: Record<string, number>; success_rate: number }>
      ('/admin/agent/task-history/stats'),

  // Decisions
  getDecisions: (status?: string) =>
    fetchAPI<{ decisions: AgentDecision[]; count: number }>
      (`/admin/agent/decisions${status ? `?status=${status}` : ''}`),

  getPendingDecisionsCount: () =>
    fetchAPI<{ pending_count: number }>('/admin/agent/decisions/count'),

  createDecision: (data: Omit<AgentDecision, 'id' | 'status' | 'created_at'>) =>
    fetchAPI('/admin/agent/decisions', { method: 'POST', body: JSON.stringify(data) }),

  resolveDecision: (id: string, selectedOption: number, rationale?: string) =>
    fetchAPI(`/admin/agent/decisions/${id}`, {
      method: 'PUT', body: JSON.stringify({ selected_option: selectedOption, rationale }),
    }),

  dismissDecision: (id: string) =>
    fetchAPI(`/admin/agent/decisions/${id}`, { method: 'DELETE' }),

  // Conversations
  getConversations: () =>
    fetchAPI<Conversation[]>('/admin/agent/conversations'),

  // GitHub
  getGitHubStatus: () =>
    fetchAPI('/admin/agent/github/status'),

  testGitHub: () =>
    fetchAPI('/admin/agent/github/test', { method: 'POST' }),

  // Scheduler
  getScheduler: () =>
    fetchAPI('/admin/agent/scheduler'),

  // Insights
  getInsights: (status?: string, insightType?: string) => {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    if (insightType) params.set('insight_type', insightType)
    const qs = params.toString()
    return fetchAPI<{ insights: unknown[]; unread_count: number }>(`/admin/agent/insights${qs ? `?${qs}` : ''}`)
  },

  markInsightRead: (id: string) =>
    fetchAPI(`/admin/agent/insights/${id}/read`, { method: 'PUT' }),

  dismissInsight: (id: string) =>
    fetchAPI(`/admin/agent/insights/${id}/dismiss`, { method: 'PUT' }),

  deleteInsight: (id: string) =>
    fetchAPI(`/admin/agent/insights/${id}`, { method: 'DELETE' }),

  // Execution preference
  getExecutionPreference: () =>
    fetchAPI<{ speed_slider: number }>('/admin/agent/execution-preference'),

  setExecutionPreference: (speedSlider: number) =>
    fetchAPI('/admin/agent/execution-preference', {
      method: 'PUT', body: JSON.stringify({ speed_slider: speedSlider }),
    }),

  // Token usage
  getTokenBudget: () =>
    fetchAPI<TokenBudget>('/admin/agent/token-usage/budget'),

  getMaxPlanStatus: () =>
    fetchAPI('/admin/agent/token-usage/max-plan'),

  bypassDailyLimit: () =>
    fetchAPI('/admin/agent/token-usage/bypass-daily', { method: 'POST' }),

  getTokenWaste: (days?: number) =>
    fetchAPI(`/admin/agent/token-usage/waste${days ? `?days=${days}` : ''}`),

  getTokenAnalytics: (days?: number) =>
    fetchAPI(`/admin/agent/token-usage/analytics${days ? `?days=${days}` : ''}`),

  // Rate limits
  getRateLimits: () =>
    fetchAPI('/admin/agent/rate-limits/status'),

  // Task streaming (SSE)
  subscribeToTaskUpdates: (
    taskId: string,
    onUpdate: (data: Record<string, unknown>) => void,
    onDone?: () => void,
  ): (() => void) => {
    const url = `${API_BASE}/admin/agent/tasks/stream?task_id=${taskId}`
    const eventSource = new EventSource(url, { withCredentials: true })

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        onUpdate(data)
        if (['completed', 'failed', 'cancelled'].includes(data.status)) {
          eventSource.close()
          onDone?.()
        }
      } catch {
        // Ignore parse errors
      }
    }

    eventSource.onerror = () => {
      eventSource.close()
      onDone?.()
    }

    return () => eventSource.close()
  },
}

// ============================================================================
// Agent System — User-facing endpoints
// ============================================================================

export const agent = {
  getConversations: () =>
    fetchAPI<Conversation[]>('/agent/conversations'),

  createTaskFromMessage: (message: string) =>
    fetchAPI<{ task_id: string; status: string; description: string }>
      ('/agent/tasks/from-message', { method: 'POST', body: JSON.stringify({ message }) }),

  getPendingApproval: () =>
    fetchAPI<{ tasks: AgentTask[] }>('/agent/tasks/pending-approval'),

  approveTask: (id: string) =>
    fetchAPI(`/agent/tasks/${id}/approve`, { method: 'POST' }),

  rejectTask: (id: string, reason?: string) =>
    fetchAPI(`/agent/tasks/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),

  getTasks: (status?: string) =>
    fetchAPI<{ tasks: AgentTask[] }>(`/agent/tasks${status ? `?status=${status}` : ''}`),

  getTask: (id: string) =>
    fetchAPI<AgentTask>(`/agent/tasks/${id}`),

  decomposeTask: (id: string) =>
    fetchAPI(`/agent/tasks/${id}/decompose`, { method: 'POST' }),

  approvePlan: (id: string) =>
    fetchAPI(`/agent/tasks/${id}/approve-plan`, { method: 'POST' }),

  getSystemHealth: () =>
    fetchAPI<SystemHealth>('/agent/system-health'),

  getHomeostasis: () =>
    fetchAPI('/agent/homeostasis'),

  getDaemonStatus: () =>
    fetchAPI('/agent/daemon/status'),

  getCapabilities: () =>
    fetchAPI('/agent/capabilities'),

  getInsights: (status?: string, insightType?: string) => {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    if (insightType) params.set('insight_type', insightType)
    const qs = params.toString()
    return fetchAPI<{ insights: unknown[]; unread_count: number }>(`/agent/insights${qs ? `?${qs}` : ''}`)
  },

  createInsight: (data: { insight_type: string; title: string; content: string; metadata?: Record<string, unknown> }) =>
    fetchAPI('/agent/insights', { method: 'POST', body: JSON.stringify(data) }),

  getDecisions: (status?: string) =>
    fetchAPI<{ decisions: AgentDecision[]; count: number }>
      (`/agent/decisions${status ? `?status=${status}` : ''}`),

  getPendingDecisionsCount: () =>
    fetchAPI<{ pending_count: number }>('/agent/decisions/count'),

  respondToDecision: (id: string, selectedOption: number, rationale?: string) =>
    fetchAPI(`/agent/decisions/${id}/respond`, {
      method: 'POST', body: JSON.stringify({ selected_option: selectedOption, rationale }),
    }),
}

// ============================================================================
// Analytics
// ============================================================================

export const analytics = {
  track: (event: Record<string, unknown>) =>
    fetchAPI('/analytics/track', { method: 'POST', body: JSON.stringify(event) }),

  trackBatch: (events: Record<string, unknown>[]) =>
    fetchAPI('/analytics/track-batch', { method: 'POST', body: JSON.stringify({ events }) }),

  getSummary: (days: number = 7, userId?: string) => {
    const params = new URLSearchParams({ days: String(days) })
    if (userId) params.set('user_id', userId)
    return fetchAPI(`/admin/analytics/summary?${params}`)
  },

  getFeatureUsage: (days: number = 7) =>
    fetchAPI(`/admin/analytics/feature-usage?days=${days}`),

  getFriction: (days: number = 7, userId?: string) => {
    const params = new URLSearchParams({ days: String(days) })
    if (userId) params.set('user_id', userId)
    return fetchAPI(`/admin/analytics/friction?${params}`)
  },

  getContentPatterns: (days: number = 7, userId?: string) => {
    const params = new URLSearchParams({ days: String(days) })
    if (userId) params.set('user_id', userId)
    return fetchAPI(`/admin/analytics/content-patterns?${params}`)
  },

  getUserJourney: (userId: string, days: number = 7) =>
    fetchAPI(`/admin/analytics/user-journey/${userId}?days=${days}`),

  getUserDashboard: (email: string, days: number = 7) =>
    fetchAPI(`/admin/analytics/user-dashboard/${email}?days=${days}`),

  getLiveSessions: () =>
    fetchAPI('/admin/analytics/live-sessions'),
}

// ============================================================================
// Feedback
// ============================================================================

export const feedback = {
  submit: (data: { category: string; severity?: string; title: string; description: string; related_page?: string }) =>
    fetchAPI<Feedback>('/feedback', { method: 'POST', body: JSON.stringify(data) }),

  list: (status?: string, category?: string) => {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    if (category) params.set('category', category)
    const qs = params.toString()
    return fetchAPI<{ feedback: Feedback[]; count: number }>(`/feedback${qs ? `?${qs}` : ''}`)
  },

  vote: (id: string) =>
    fetchAPI<{ id: string; vote_count: number }>(`/feedback/${id}/vote`, { method: 'POST' }),
}

// ============================================================================
// Frontend Error Reporting
// ============================================================================

export function reportFrontendError(data: {
  component: string
  error_message: string
  component_stack?: string
  url?: string
}) {
  return fetchAPI('/frontend-error', {
    method: 'POST',
    body: JSON.stringify({
      ...data,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    }),
  }).catch(() => { /* Best-effort, don't throw */ })
}

// ============================================================================
// Brain API (Agent Infrastructure)
// ============================================================================

export const brain = {
  getHealth: () =>
    fetchBrain<BrainHealth>('/api/health'),

  getDaemonStates: () =>
    fetchBrain<DaemonState[]>('/api/state/'),

  submitTask: (data: {
    type: string
    priority: string
    title?: string
    payload: Record<string, unknown>
    metadata?: Record<string, unknown>
  }) =>
    fetchBrain('/api/tasks/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
}

// ============================================================================
// Paradigm-specific endpoints (existing production API)
// ============================================================================

export const paradigm = {
  getIdeas: () =>
    fetchAPI<Array<{ id: string; name: string; canon_files: string[]; phase: string }>>('/ideas'),

  getCanonDocument: (idea: string, doc: string) =>
    fetchAPI<{ idea: string; document: string; content: string; last_modified: string; line_count: number }>
      (`/ideas/${idea}/canon/${doc}`),

  getTodos: (idea?: string, category?: string) => {
    const params = new URLSearchParams()
    if (idea) params.set('idea', idea)
    if (category) params.set('category', category)
    const qs = params.toString()
    return fetchAPI(`/todos${qs ? `?${qs}` : ''}`)
  },

  resolveTodo: (todoId: string, selectedOption: number | null, notes?: string, skipValidation?: boolean) =>
    fetchAPI(`/todos/${todoId}/resolve`, {
      method: 'POST', body: JSON.stringify({
        selected_option: selectedOption,
        notes,
        response_text: notes,
        skip_validation: skipValidation || false,
      }),
    }),

  batchResolveTodos: (todoIds: string[], selectedOption: number, notes?: string) =>
    fetchAPI<{ resolved_count: number; not_found_count: number; error_count: number; resolved: string[]; not_found: string[]; errors: Array<{ id: string; error: string }> }>('/todos/batch-resolve', {
      method: 'POST', body: JSON.stringify({ todo_ids: todoIds, selected_option: selectedOption, notes }),
    }),

  sendChatMessage: (message: string, mode: string = 'NORMAL_CHAT') =>
    fetchAPI<{ agent_response: string }>('/chat', {
      method: 'POST', body: JSON.stringify({ mode, message }),
    }),

  getMethodologyExtract: (ideaName: string) =>
    fetchAPI(`/methodology/extract/${ideaName}`),

  getSafetyAudit: () =>
    fetchAPI('/safety/audit'),

  checkSafetyPath: (path: string) =>
    fetchAPI(`/safety/check-path?path=${encodeURIComponent(path)}`),

  getControlPlaneStatus: () =>
    fetchAPI('/admin/control-plane/status'),
}

// ============================================================================
// Legacy compatibility — keep old function names working
// ============================================================================

export const fetchBrainHealth = brain.getHealth
export const fetchDaemonState = brain.getDaemonStates
export const fetchIdeas = paradigm.getIdeas
export const fetchCanonDocument = paradigm.getCanonDocument
export const fetchTodos = paradigm.getTodos
export const resolveTodo = paradigm.resolveTodo
export const sendChatMessage = paradigm.sendChatMessage
